import type { OrganizationInvitation, OrganizationInvitationCreateParams } from "@mountaya/client";
import { z } from "zod";

import {
  bodyRefusal,
  conflictMetadataOf,
  isAnswerUnavailableError,
  isRateLimitError,
  isSeatLimitError,
  isWriteStillRunningError,
  limitReachedOf,
  type RestClientConfig,
  retryAfterOf,
} from "../../rest/rules/sdk";
import { createOrganizationInvitation } from "./mutations";

/**
 * EMAIL_FIELD is the member of an invitation's body a refused address is answered at:
 * `["request", "body", "email"]`.
 */
const EMAIL_FIELD = "email";

/**
 * InvitationRefusal is why one address was not invited, read off the API's answer to it.
 *
 * - `member` — the address already belongs to a member of the organization.
 * - `invited` — an invitation to the address is already waiting for an answer.
 * - `address` — the address itself was refused (a 400 at the body's `email`), in the API's own
 *   sentence.
 * - `seats` — the agreement has no seat left: members and pending invitations hold one each.
 * - `pendingInvitations` — the organization already has as many invitations waiting as it may.
 * - `rateLimited` — too many invitations were sent just now; `retryAfter` is the wait in seconds,
 *   where the answer named one.
 * - `stillSending` — the invitation is still being made by the request first sent for it, which
 *   the client waited on until its wait was spent (409 `IDEMPOTENCY_KEY_IN_USE`). It is no
 *   refusal of the address, and sending it again is not the way on: it shows among the pending
 *   invitations once made.
 * - `failed` — anything else, which the surface reports in its own generic words.
 *
 * Every well-formed address is answered alike whether or not an account carries it, so there is
 * no "this address has no account" refusal: an address nobody has signed up with is invited and
 * waits for the person to sign up.
 */
export type InvitationRefusal =
  | { readonly kind: "member" }
  | { readonly kind: "invited" }
  | { readonly kind: "address"; readonly message: string }
  | { readonly allowed: number | undefined; readonly kind: "seats" }
  | { readonly allowed: number | undefined; readonly kind: "pendingInvitations" }
  | { readonly kind: "rateLimited"; readonly retryAfter: number | undefined }
  | { readonly kind: "stillSending" }
  | { readonly error: unknown; readonly kind: "failed" };

/**
 * invitationConflictSchema parses the metadata a 409 to an invitation carries: the member the
 * address belongs to, or the invitation already waiting for it. The two idempotency 409s carry
 * neither, and are read before it: `IDEMPOTENCY_KEY_IN_USE`, which the client raises once its own
 * wait for the first request is spent (`stillSending`), and `IDEMPOTENCY_ANSWER_UNAVAILABLE`,
 * which says the invitation was made ({@link inviteOrganizationMembers} reads it as `made`).
 */
const invitationConflictSchema = z.object({
  invitation: z.object({ id: z.string() }).optional(),
  member: z.object({ user_id: z.string() }).optional(),
});

/**
 * invitationRefusalOf classifies what a single invitation was refused with. The order follows
 * the API's own: the address first, then the conflict, then the ceilings, then the rate limit.
 * An invitation still being made is read before the conflict, whose status it shares. One whose
 * answer was lost after it was made is no refusal at all: the walk reads it as `made` before it
 * asks this, and anything that still hands it here gets `failed`, never `member` or `invited`.
 */
export const invitationRefusalOf = (error: unknown): InvitationRefusal => {
  const refused = bodyRefusal(error, 400, EMAIL_FIELD);
  if (refused !== undefined) {
    return { kind: "address", message: refused.message };
  }

  if (isWriteStillRunningError(error)) {
    return { kind: "stillSending" };
  }

  if (isAnswerUnavailableError(error)) {
    return { error, kind: "failed" };
  }

  const conflict = invitationConflictSchema.safeParse(conflictMetadataOf(error));
  if (conflict.success && conflict.data.member !== undefined) {
    return { kind: "member" };
  }

  if (conflict.success && conflict.data.invitation !== undefined) {
    return { kind: "invited" };
  }

  const limit = limitReachedOf(error);
  if (isSeatLimitError(error)) {
    return { allowed: limit?.allowed, kind: "seats" };
  }

  if (limit?.limit === "pending-invitations") {
    return { allowed: limit.allowed, kind: "pendingInvitations" };
  }

  if (isRateLimitError(error)) {
    return { kind: "rateLimited", retryAfter: retryAfterOf(error) };
  }

  return { error, kind: "failed" };
};

/**
 * InvitationOutcome is what became of one address a form asked to invite.
 *
 * - `invited` carries the invitation as made.
 * - `made` is an invitation that was made but whose answer was lost on its way back: the API
 *   answered a 5xx after the invitation was stored, and the client's repeat of the request was
 *   answered 409 `IDEMPOTENCY_ANSWER_UNAVAILABLE`. The address was invited all the same, and the
 *   pending invitations, read again once the walk ends, list it.
 * - `refused` carries why ({@link InvitationRefusal}).
 * - `skipped` is an address never sent, because an earlier one met a refusal that would have
 *   refused it too: no seat left, the pending invitations at their ceiling, or the rate limit.
 */
export type InvitationOutcome =
  | {
      readonly email: string;
      readonly invitation: OrganizationInvitation;
      readonly status: "invited";
    }
  | { readonly email: string; readonly status: "made" }
  | { readonly email: string; readonly refusal: InvitationRefusal; readonly status: "refused" }
  | { readonly email: string; readonly status: "skipped" };

/**
 * STOPS_THE_REST are the refusals that hold for every address after the one that met them: a
 * ceiling of the organization, or the rate limit. Sending the rest would only collect the same
 * answer again, and spend the rate limit doing it.
 */
const STOPS_THE_REST: ReadonlySet<InvitationRefusal["kind"]> = new Set([
  "pendingInvitations",
  "rateLimited",
  "seats",
]);

/**
 * inviteOrganizationMembers invites every address asked for, one request each, as the API takes
 * them, and answers what became of each, in the order asked.
 *
 * The requests are sent one after the other rather than together. Two invitations sent at the
 * same moment cannot both take the last seat, so one of them would be refused for a seat the
 * other ended up not needing, and the rate limit counts each request: a burst spends it at once.
 * A refusal that holds for every address after it stops the walk, and the addresses left are
 * reported `skipped`; any other refusal is reported for its own address and the walk goes on.
 */
export const inviteOrganizationMembers = async (
  config: RestClientConfig,
  organizationId: string,
  invitations: readonly OrganizationInvitationCreateParams[],
): Promise<InvitationOutcome[]> => {
  const outcomes: InvitationOutcome[] = [];
  let stopped = false;

  for (const params of invitations) {
    if (stopped) {
      outcomes.push({ email: params.email, status: "skipped" });
      continue;
    }

    try {
      const invitation = await createOrganizationInvitation(config, organizationId, params);
      outcomes.push({ email: params.email, invitation, status: "invited" });
    } catch (error: unknown) {
      // The invitation was made and its answer lost: it is an invitation, never a failure, and
      // sending it again under a new key would be refused as already invited at best.
      if (isAnswerUnavailableError(error)) {
        outcomes.push({ email: params.email, status: "made" });
        continue;
      }

      const refusal = invitationRefusalOf(error);
      outcomes.push({ email: params.email, refusal, status: "refused" });
      stopped = STOPS_THE_REST.has(refusal.kind);
    }
  }

  return outcomes;
};
